import { useCallback, useEffect, useState } from 'react';

import { useAtomValue } from 'jotai';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import type { Filter, FilterKey } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { getFilterFromArray } from '@core/Utils/SearchFilters/GetFilterFromArray';

import { trackFilterEvent } from 'Components/Plp/Filters/Filters.tracking';
import { useUpdateFilters } from 'Hooks/SearchFilters/UseUpdateFilters';
import { getClosestIndexInRange } from 'Utils/SearchFilters/GetClosestIndexInRange';

/**
 * Custom hook for single value range filter (min only)
 */
export const useSingleValueFilter = (filterName: string, range: number[], defaultValue = '') => {
  const filtersList = useAtomValue(filtersListAtom);
  const { updateFilters } = useUpdateFilters();
  const gteId = `${filterName}__gte` as FilterKey;

  // Get initial value from filters or use default
  const initialValue =
    (getFilterFromArray(gteId, filtersList)?.value as string | undefined) || defaultValue;
  const [value, setValue] = useState(initialValue);

  // Update when filters change
  useEffect(() => {
    const currentValue = getFilterFromArray(gteId, filtersList)?.value as string | undefined;
    setValue(currentValue || defaultValue);
  }, [filtersList, gteId, defaultValue]);

  // Handle input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  }, []);

  // Handle input blur (commit value)
  const handleInputBlur = useCallback(() => {
    // If value is "0", remove the filter
    const filterValue = value === '0' ? undefined : value;
    const filterChange: Filter = { id: gteId, value: filterValue };
    trackFilterEvent(gteId, [value], filtersList);
    updateFilters([filterChange]);
  }, [gteId, value, updateFilters, filtersList]);

  // Handle slider change
  const handleSliderChange = useCallback(
    (values: number[]) => {
      if (values && values.length > 0) {
        setValue(range[values[0]].toString());
      }
    },
    [range],
  );

  // Handle slider after change (commit value)
  const handleSliderAfterChange = useCallback(
    (values: number[]) => {
      if (values && values.length > 0) {
        const newValue = range[values[0]].toString();
        // If value is "0", remove the filter
        const filterValue = newValue === '0' ? undefined : newValue;
        const filterChange: Filter = { id: gteId, value: filterValue };
        trackFilterEvent(gteId, [newValue], filtersList);
        updateFilters([filterChange]);
      }
    },
    [gteId, range, updateFilters, filtersList],
  );

  // Check if can reset
  const canReset = useCallback(() => {
    return getFilterFromArray(gteId, filtersList) !== undefined;
  }, [filtersList, gteId]);

  // Reset the filter
  const reset = useCallback(() => {
    setValue(defaultValue);
    updateFilters([{ id: gteId, value: undefined }]);
  }, [gteId, defaultValue, updateFilters]);

  // Calculate slider props
  const sliderStepCount = range.length;
  const valueIndex = getClosestIndexInRange(Number(value), range);

  return {
    inputProps: {
      value,
      onChange: handleInputChange,
      onBlur: handleInputBlur,
    },
    sliderProps: {
      onChange: handleSliderChange,
      onAfterChange: handleSliderAfterChange,
      stepCount: sliderStepCount,
      // For a single knob slider, we only use the first index
      selectedRangeIndexes: [valueIndex, valueIndex],
      value: [Number(value || defaultValue)],
    },
    canReset,
    reset,
  };
};
